import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { forgotPassword } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email);
      // Always shown regardless of whether the email matched an account —
      // the backend never reveals that either.
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2 text-slate-900 font-bold text-[16px] mb-10">
          <Building2 size={20} className="text-sky-500" />
          ScapeSource
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <h1 className="text-[26px] font-extrabold tracking-tight text-slate-900">Check your email</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">
              If <strong>{email}</strong> is registered, we've sent a link to reset the password. It expires in 1
              hour and can only be used once.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-[26px] font-extrabold tracking-tight text-slate-900">Reset your password</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">
              Enter your work email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[13px] font-medium text-slate-700">Work email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="forgot-password-email-input"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </div>

              {error && (
                <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-button"
                className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold text-[14px] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}

        <Link to="/login" className="block text-[13px] text-sky-600 font-medium mt-6">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
